/******************************************************************************
 *
 * Copyright (c) 2019-2022 ServiceActivator Inc. All Rights Reserved.
 *
 * Unauthorized copying of the code in this file, completely or partially,
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 *
 * Written by 'Shivam Mishra' <shivam.mishra@wakencode.com>, June 2022
 *
 *****************************************************************************/

/**
 * Format the data in a correct to to send in request body
 *
 * @returns Formatted request body for create account
 */
export const prepareMetaData = () => {
  let metadata = {};
  // Get query params data for metadata
  let params = new URLSearchParams(window.location.search);
  let entries = params.entries();

  for (let entry of entries) {
    if (!entry[0]) continue;
    metadata[`${entry[0]}`] = entry[1] ? entry[1] : null;
  }
  metadata.userAgent = window.navigator.userAgent;

  return metadata;
};

/**
 * Prepare personal details for request body
 *
 * @param {object} userInfo User personal details
 * @param {object} userData user data from context
 * @returns Correct format of request body
 */
export const preparePersonalDetailsPostData = (userInfo, userData) => {
  let postData = {
    journeyStage: userData?.journeyStage || "S1",
    acceptedTermsAndConditionsVersion: userData?.acceptedTermsAndConditionsVersion || null,
    signupWebAppVersion: userData?.signupWebAppVersion || null,
    invitationId: userData?.invitationId || null,
    userDetails: {
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      email: userInfo.email,
      employeeRole: userInfo.employeeRole,
    },
    subscriberDetails: {
      type: userData?.subscriberDetails?.type || null,
      name: userData?.subscriberDetails?.name || null,
      address: {
        zipCode: userData?.subscriberDetails?.address?.zipCode || null,
      },
    },
    employeeCount: userData?.employeeCount || null,
    verifiedEmail: userData?.verifiedEmail || null,
    isEmailVerified: userData?.isEmailVerified || false,
  };
  return postData;
};

/**
 * Prepare owner personal details for request body
 *
 * @param {object} userInfo User personal details
 * @param {object} userData user data from context
 * @returns Formatted request body
 */
export const prepareOwnerPostData = (userInfo, userData) => {
  let postData = {
    journeyStage: userData?.journeyStage || "S2",
    acceptedTermsAndConditionsVersion: userData?.acceptedTermsAndConditionsVersion || null,
    signupWebAppVersion: userData?.signupWebAppVersion || null,
    invitationId: userData?.invitationId || null,
    userDetails: {
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      email: userInfo.email,
      employeeRole: userInfo.employeeRole,
    },
    subscriberDetails: {
      type: userData?.subscriberDetails?.type || null,
      name: userData?.subscriberDetails?.name || null,
      address: {
        zipCode: userData?.subscriberDetails?.address?.zipCode || null,
      },
    },
    employeeCount: userData?.employeeCount || null,
    verifiedEmail: userData?.verifiedEmail || null,
    isEmailVerified: userData?.isEmailVerified || false,
  };
  return postData;
};

/**
 * Prepare work details employee count for request body for user signup
 *
 * @param {object} userData user data from context
 * @param {object} companySize company size details object
 * @returns Formatted request body
 */
export const prepareWorkDetails = (userData, companySize) => {
  let postData = {
    journeyStage: userData?.journeyStage || "S3",
    acceptedTermsAndConditionsVersion: userData?.acceptedTermsAndConditionsVersion || null,
    signupWebAppVersion: userData?.signupWebAppVersion || null,
    invitationId: userData?.invitationId || null,
    userDetails: {
      firstName: userData.userDetails.firstName,
      lastName: userData.userDetails.lastName,
      email: userData.userDetails.email,
      employeeRole: userData.userDetails.employeeRole,
    },
    subscriberDetails: {
      type: companySize.userType,
      name: userData?.subscriberDetails?.name || null,
      address: {
        zipCode: userData?.subscriberDetails?.address?.zipCode || null,
      },
    },
    employeeCount: companySize.employeeCount,
    verifiedEmail: userData?.verifiedEmail,
    isEmailVerified: userData?.isEmailVerified,
  };
  return postData;
};

/**
 * Work details information for owner operator for not invited user
 *
 * @param {object} userData user data from userContext
 * @param {object} companyInfo Work details information of user
 * @returns Formatted request body
 */
export const prepareCompanyDetails = (userData, companyInfo) => {
  let postData = {
    journeyStage: userData?.journeyStage || "S4",
    acceptedTermsAndConditionsVersion: userData?.acceptedTermsAndConditionsVersion || null,
    signupWebAppVersion: userData?.signupWebAppVersion || null,
    invitationId: userData?.invitationId || null,
    userDetails: {
      firstName: userData.userDetails.firstName,
      lastName: userData.userDetails.lastName,
      email: userData.userDetails.email,
      employeeRole: userData.userDetails.employeeRole,
    },
    subscriberDetails: {
      type: userData?.subscriberDetails?.type,
      name: companyInfo.subscriberName,
      address: {
        zipCode: companyInfo.zipCode,
      },
    },
    employeeCount: userData?.employeeCount,
    verifiedEmail: userData?.verifiedEmail,
    isEmailVerified: userData?.isEmailVerified,
  };
  return postData;
};

/**
 * Subscriber Owner information filled by a new employee
 *
 * @param {object} userData user data from userContext
 * @param {object} ownerInfo subscriber owner details
 * @returns Formatted request body
 */
export const prepareOwnerSubscriberDetails = (userData, ownerInfo) => {
  let postData = {
    journeyStage: userData?.journeyStage || "S5",
    acceptedTermsAndConditionsVersion: userData?.acceptedTermsAndConditionsVersion || null,
    signupWebAppVersion: userData?.signupWebAppVersion || null,
    invitationId: userData?.invitationId || null,
    userDetails: {
      firstName: userData.userDetails.firstName,
      lastName: userData.userDetails.lastName,
      email: userData.userDetails.email,
      employeeRole: userData.userDetails.employeeRole,
    },
    subscriberDetails: {
      type: userData?.subscriberDetails?.type,
      name: userData?.subscriberDetails?.name,
      address: {
        zipCode: userData?.subscriberDetails?.address?.zipCode,
      },
    },
    subscriberOwnerDetails: {
      firstName: ownerInfo.firstName,
      lastName: ownerInfo.lastName,
      email: ownerInfo.email,
      mobile: ownerInfo.mobile,
    },
    employeeCount: userData?.employeeCount,
    verifiedEmail: userData?.verifiedEmail,
    isEmailVerified: userData?.isEmailVerified,
  };
  return postData;
};


/**
 * Update journey stage on activation
 * @param {object} userData user data from userContext
*/
export const updateJourneyStage = (userData) => {
  
  return {...userData, journeyStage: "S100"};
}